import React, { FC, useState } from 'react';
import styled, { keyframes, useTheme } from 'styled-components';

import colors from '../../utils/colors';
import MaterialIcon from './icons/MaterialIcon';
import { readableTextColor } from '../../utils/colorUtils';

type Props = {
  checked: boolean;
  onChange: () => void;
  /** The course's colour for the empty ring, as on the task's own; grey without one. */
  colorHex?: string;
  label?: string;
  size?: number;
  className?: string;
};

/**
 * A round tick, like a task's complete ring in the Task List (Petr: subtasks read like tasks): an empty ring, green with a
 * white check once ticked, and a click unticks it again.
 */
const TickRing: FC<Props> = ({ checked, onChange, colorHex, label, size = 18, className }) => {
  const theme: any = useTheme();
  const [justTicked, setJustTicked] = useState(false);
  const color = colorHex && theme?.background ? readableTextColor(colorHex, theme.background, 2.5) : colorHex || colors.gray;
  return (
    <Ring
      type={'button'}
      role={'checkbox'}
      aria-checked={checked}
      aria-label={label}
      className={`tick-ring${className ? ` ${className}` : ''}${checked ? ' checked' : ''}${
        justTicked ? ' just-ticked' : ''
      }`}
      $color={color}
      $size={size}
      onClick={() => {
        setJustTicked(!checked);
        onChange();
      }}
      onAnimationEnd={() => setJustTicked(false)}
    >
      <MaterialIcon name={'check'} size={Math.round(size * 0.8)} />
    </Ring>
  );
};

export default TickRing;

const pop = keyframes`
  0% {
    transform: scale(0.6);
  }
  60% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
`;

const Ring = styled.button<{ $color: string; $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1.5px solid ${props => props.$color};
  background: transparent;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  i {
    color: transparent;
    transition: color 0.15s ease;
  }
  &:hover {
    border-color: ${colors.positive};
    background: color-mix(in srgb, ${colors.positive} 16%, transparent);
    i {
      color: ${colors.positive};
    }
  }
  &.checked {
    border-color: ${colors.positive};
    background: ${colors.positive};
    i {
      color: ${colors.white};
    }
    &:hover {
      background: color-mix(in srgb, ${colors.positive} 75%, transparent);
    }
  }
  &.just-ticked i {
    animation: ${pop} 0.3s ease-out;
  }
  &:focus-visible {
    outline: 2px solid ${props => props.theme.primary};
    outline-offset: 1px;
  }
`;
